import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const AVATARS = [
  "https://ui-avatars.com/api/?name=Yeshwanth+Reddy&background=FF5200&color=fff&bold=true&size=200",
  "https://ui-avatars.com/api/?name=Harishwar+Reddy&background=1D1D1F&color=fff&bold=true&size=200",
  "https://ui-avatars.com/api/?name=Karthikeya+Kolla&background=FF5200&color=fff&bold=true&size=200",
  "https://ui-avatars.com/api/?name=Adarsh+Bayya&background=1D1D1F&color=fff&bold=true&size=200",
  "https://ui-avatars.com/api/?name=Saivarma+P&background=FF5200&color=fff&bold=true&size=200",
  "https://ui-avatars.com/api/?name=Junaid+Mohammed&background=1D1D1F&color=fff&bold=true&size=200",
  "https://ui-avatars.com/api/?name=Karthik+Reddy&background=FF5200&color=fff&bold=true&size=200",
  "https://ui-avatars.com/api/?name=Manideep+Reddy&background=1D1D1F&color=fff&bold=true&size=200",
];

const TESTIMONIALS = [
  { quote: "After struggling for months, ApplyStrat gave me back my time. I focused entirely on interview prep and DSA. Got my first call from Google within 5 weeks. Completely changed the game.", name: "Yeshwanth Reddy", role: "Software Engineer · Google", img: AVATARS[0] },
  { quote: "No 'guaranteed results' pitch — just clear strategy, real applications through official portals, and honest weekly check-ins. That kind of transparency is rare and builds real trust.", name: "Harishwar Reddy", role: "Software Engineer · Apple", img: AVATARS[1] },
  { quote: "The resume rewrite alone changed how I was perceived. Roles I'd applied to before with zero response started getting back to me within two weeks of starting with ApplyStrat.", name: "Karthikeya Kolla", role: "ML Engineer · Tesla", img: AVATARS[2] },
  { quote: "I was spending 4+ hours a day just applying. Now that time goes into system design prep. ApplyStrat handles the volume; I focus on the conversations that matter.", name: "Adarsh Bayya", role: "SDE · Amazon", img: AVATARS[3] },
  { quote: "The weekly strategy calls helped me realize I was targeting the wrong roles. After one pivot, response rates tripled. That kind of insight is priceless.", name: "Saivarma P", role: "Software Engineer · LinkedIn", img: AVATARS[4] },
  { quote: "What sets ApplyStrat apart is the honesty. They told me upfront what to expect, and delivered exactly that — no noise, no fluff, just a clean process that works.", name: "Junaid Mohammed", role: "SDE II · Microsoft", img: AVATARS[5] },
  { quote: "My LinkedIn profile optimization alone brought in two inbound messages from recruiters. Combined with the application volume, it created real momentum in my search.", name: "Karthik Reddy", role: "Software Engineer · PayPal", img: AVATARS[6] },
  { quote: "I was skeptical at first. But the systematic approach — official portals only, strategy reviews, no shortcuts — made me feel confident this was the right way to do it.", name: "Manideep Reddy", role: "SDE · Intuit", img: AVATARS[7] },
];

export default function TestimonialsSection() {
  return (
    <>
      {/* ── TESTIMONIALS ── */}
      <section className="py-28 md:py-36 bg-surface-raised overflow-hidden" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp()} className="text-center max-w-2xl mx-auto mb-4">
            <p className="section-label-orange mb-3">Candidate Experiences</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink">Results That Speak</h2>
          </motion.div>
          <motion.p {...fadeUp(0.05)} className="text-center text-ink-secondary text-xs mb-12">These are candidates who worked with us. Company names reflect their target/placed employers — ApplyStrat is not affiliated with or endorsed by these companies.</motion.p>
          <motion.div {...fadeUp(0.1)}>
            <Carousel opts={{ loop: true, align: "start" }} plugins={[Autoplay({ delay: 3800, stopOnInteraction: true })]} className="w-full">
              <CarouselContent className="-ml-5">
                {TESTIMONIALS.map((t) => (
                  <CarouselItem key={t.name} className="pl-5 md:basis-1/2 lg:basis-1/3">
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-black/5 hover:shadow-[0_8px_30px_rgba(255,82,0,0.08)] hover:-translate-y-1 transition-all duration-400 flex flex-col h-full">
                      <div className="flex gap-1 mb-5">{[...Array(5)].map((_, j) => <Star key={j} size={14} className="text-brand fill-brand" />)}</div>
                      <p className="text-ink-secondary text-sm leading-relaxed flex-1 mb-6">"{t.quote}"</p>
                      <div className="flex items-center gap-3">
                        <img src={t.img} alt={`${t.name} testimonial`} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                        <div>
                          <p className="text-ink font-medium text-sm">{t.name}</p>
                          <p className="text-ink-secondary text-xs">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </motion.div>
        </div>
      </section>

      {/* ── FREE TOOLS CTA ── */}
      <section className="py-20 bg-white" data-testid="free-tools-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div {...fadeUp()} className="bg-surface-raised rounded-3xl p-8 hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] transition-all duration-400 group">
              <span className="text-3xl font-medium text-brand/20 block mb-4">AI</span>
              <h3 className="text-ink font-medium text-lg mb-3">Optimize Your Resume with AI — Free</h3>
              <p className="text-ink-secondary text-sm leading-relaxed mb-6">Use our expert-crafted 9-step AI prompt to get a full ATS audit, language rewrite, and polished resume in minutes.</p>
              <Link to="/resume-optimizer" data-testid="home-optimizer-link" className="inline-flex items-center gap-2 text-brand font-medium text-sm hover:text-brand-hover transition-colors">
                Try the Master Prompt <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div {...fadeUp(0.1)} className="bg-surface-raised rounded-3xl p-8 hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] transition-all duration-400 group">
              <span className="text-3xl font-medium text-brand/20 block mb-4">10</span>
              <h3 className="text-ink font-medium text-lg mb-3">10 Prompts I Used to Fix My Resume</h3>
              <p className="text-ink-secondary text-sm leading-relaxed mb-6">Targeted prompts for every resume section — summaries, bullet points, ATS keywords, career transitions, and more.</p>
              <Link to="/blog/10-prompts-fix-resume" data-testid="home-blog-link" className="inline-flex items-center gap-2 text-brand font-medium text-sm hover:text-brand-hover transition-colors">
                Read the guide <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FOUNDER ── */}
      <section className="py-28 md:py-32 bg-surface-raised" data-testid="founder-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <p className="section-label-orange mb-3">The Team</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink">Built by People Who Care</h2>
          </motion.div>
          <motion.div {...fadeUp(0.1)} className="bg-white rounded-3xl p-10 shadow-sm border border-black/5 max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8">
              <img
                src="https://customer-assets.emergentagent.com/job_serious-applications/artifacts/e03ypy9t_WhatsApp%20Image%202026-05-23%20at%2011.52.05%20AM.jpeg"
                alt="Srinivas Nakka"
                className="w-28 h-28 rounded-2xl object-cover object-top flex-shrink-0 shadow-md"
                onError={(e) => { e.target.onerror = null; e.target.src = "https://ui-avatars.com/api/?name=Srinivas+Nakka&background=FF5200&color=fff&bold=true&size=200"; }}
              />
              <div>
                <h3 className="text-ink font-medium text-xl mb-1">Srinivas Nakka</h3>
                <p className="text-brand font-medium text-sm mb-4">Managing Director · People's Leader</p>
                <p className="text-ink-secondary text-sm leading-relaxed">
                  Srinivas leads ApplyStrat's strategy and operations — focused on helping serious tech candidates cut through job-search noise with structured, honest execution. His people-first approach drives every candidate experience at ApplyStrat.
                </p>
                <a href="mailto:srinivas@applystrat.com" className="inline-flex items-center gap-2 mt-4 text-brand text-sm font-medium hover:text-brand-hover transition-colors">
                  srinivas@applystrat.com
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
